import React, { useState, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import MessageBox from './MessageBox';

const API_BASE_URL = window.API_BASE_URL;

export default function CreatePaper({ currentUser }) {
  const editorRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    class: '',
    subject: '',
    date: ''
  });
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ show: false, text: '', type: 'info' });
  const [showSaveModal, setShowSaveModal] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditorChange = (content, editor) => {
    setContent(content);
  };

  const handleSaveClick = () => {
    // Check if content exists before showing modal
    if (!content || content.trim() === '' || content === '<p></p>') {
      setMessage({ show: true, text: 'Please add content to the paper first', type: 'error' });
      return;
    }
    setShowSaveModal(true);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.title || !formData.class || !formData.subject || !formData.date) {
      setMessage({ show: true, text: 'Please fill in all fields', type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/subjective-papers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          content
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to save paper');
      }

      setMessage({ show: true, text: 'Paper saved successfully!', type: 'success' });
      setShowSaveModal(false);
      
      // Reset form
      setFormData({
        title: '',
        class: '',
        subject: '',
        date: ''
      });
      setContent('');
      if (editorRef.current) {
        editorRef.current.setContent('');
      }
    } catch (error) {
      console.error('Error saving paper:', error);
      setMessage({ show: true, text: error.message || 'Failed to save paper', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '1600px', margin: '0' }}>
      {message.show && (
        <MessageBox
          message={message.text}
          type={message.type}
          onClose={() => setMessage({ show: false, text: '', type: 'info' })}
        />
      )}

      <div style={{
        background: 'var(--panel-bg, #ffffff)',
        border: '1px solid var(--border, #e0e0e0)',
        borderRadius: '12px',
        padding: '1px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        marginBottom: '20px'
      }}>
        {/* Rich Text Editor */}
        <div style={{ marginBottom: '20px' }}>
          <Editor
            apiKey="8h1sqec5a1s49emfs5ctml6o6vqo35fz2kta2qmnf602zhfa"  // Replace with your actual TinyMCE API key
            onInit={(evt, editor) => editorRef.current = editor}
            value={content}
            onEditorChange={handleEditorChange}
            init={{
              height: 800,
              menubar: true,
              plugins: [
                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'media', 'table', 'help', 'wordcount'
              ],
              toolbar: 'undo redo | blocks | ' +
                'bold italic underline strikethrough | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | ' +
                'removeformat | image | help',
              content_style: "body { font-family:'Noto Nastaliq Urdu',Arial,sans-serif; font-size:12pt; direction:rtl; }",
              paste_data_images: true, // Allow pasting images as base64
              automatic_uploads: true, // Automatically upload images
              images_upload_handler: (blobInfo, progress) => new Promise((resolve, reject) => {
                // Convert image to base64
                const reader = new FileReader();
                reader.readAsDataURL(blobInfo.blob());
                reader.onloadend = () => {
                  resolve(reader.result);
                };
                reader.onerror = () => {
                  reject('Error reading file');
                };
              }),
              // Handle pasted images with external URLs
              paste_preprocess: async (plugin, args) => {
                // Find all img tags in pasted content
                const parser = new DOMParser();
                const doc = parser.parseFromString(args.content, 'text/html');
                const images = doc.querySelectorAll('img');
                
                for (const img of images) {
                  const src = img.getAttribute('src');
                  // If image has external URL, try to convert to base64
                  if (src && (src.startsWith('http://') || src.startsWith('https://'))) {
                    try {
                      // Fetch the image and convert to base64
                      const response = await fetch(src);
                      const blob = await response.blob();
                      const reader = new FileReader();
                      const base64 = await new Promise((resolve) => {
                        reader.onloadend = () => resolve(reader.result);
                        reader.readAsDataURL(blob);
                      });
                      img.setAttribute('src', base64);
                    } catch (error) {
                      console.error('Failed to convert external image to base64:', error);
                      // Keep original URL if conversion fails
                    }
                  }
                }
                
                args.content = doc.body.innerHTML;
              }
            }}
          />
        </div>

        {/* Save Button */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleSaveClick}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '14px',
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <i className="fa-solid fa-floppy-disk"></i>
            Save Paper
          </button>
        </div>
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--panel-bg, #ffffff)',
            borderRadius: '16px',
            maxWidth: '500px',
            width: '100%',
            padding: '28px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.4rem', fontWeight: 700 }}>
                <i className="fa-solid fa-file-circle-plus" style={{ marginRight: '10px', color: '#10b981' }}></i>
                Save Paper
              </h2>
              <button
                onClick={() => setShowSaveModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                  padding: '4px 8px',
                  borderRadius: '6px'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>
                  <i className="fa-solid fa-heading" style={{ marginRight: '8px', color: '#3B82F6' }}></i>
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Final Exam 2024"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid var(--border, #e0e0e0)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'var(--input-bg, white)',
                    color: 'var(--text-primary)',
                    transition: 'border-color 0.2s'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>
                    <i className="fa-solid fa-graduation-cap" style={{ marginRight: '8px', color: '#8B5CF6' }}></i>
                    Class *
                  </label>
                  <input
                    type="text"
                    name="class"
                    value={formData.class}
                    onChange={handleInputChange}
                    placeholder="e.g., 10"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '2px solid var(--border, #e0e0e0)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'var(--input-bg, white)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>
                    <i className="fa-solid fa-book" style={{ marginRight: '8px', color: '#10b981' }}></i>
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="e.g., Physics"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '2px solid var(--border, #e0e0e0)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'var(--input-bg, white)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>
                  <i className="fa-solid fa-calendar" style={{ marginRight: '8px', color: '#F59E0B' }}></i>
                  Date *
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid var(--border, #e0e0e0)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'var(--input-bg, white)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '28px' }}>
              <button
                onClick={() => setShowSaveModal(false)}
                style={{
                  padding: '12px 24px',
                  background: 'var(--surface, #f5f5f5)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border, #ddd)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  background: loading ? '#ccc' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '14px',
                  boxShadow: loading ? 'none' : '0 2px 8px rgba(16, 185, 129, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <i className="fa-solid fa-check"></i>
                {loading ? 'Saving...' : 'Save Paper'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
